import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  GitBranch,
  Link as LinkIcon,
  Plus,
  Save,
  Tag,
  Trash2,
} from 'lucide-react';
import {
  useVaultTree,
  useObsidianFile,
  useObsidianTags,
  useGitLog,
  useGitStatus,
} from '@/hooks/useObsidian';
import { useFileHash, useUpdateFile, useDeleteFile } from '@/hooks/useObsidianWrite';
import { VaultTree } from '@/components/VaultTree';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { MarkdownEditor } from '@/components/MarkdownEditor';
import { NewDocModal } from '@/components/NewDocModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';

export function ObsidianPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const path = params.get('path');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftHash, setDraftHash] = useState<string | null>(null);
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const { data: tree } = useVaultTree();
  const { data: file, isLoading: fileLoading } = useObsidianFile(path);
  const { data: hashData } = useFileHash(path);
  const { data: tagsData } = useObsidianTags();
  const { data: commits } = useGitLog(path);
  const { data: gitStatus } = useGitStatus();
  const updateFile = useUpdateFile();
  const deleteFile = useDeleteFile();

  const dirty = editing && draft !== (file?.file.raw ?? '');

  function openFile(newPath: string): void {
    if (dirty && !confirm('Descartar alterações não salvas?')) return;
    setParams({ path: newPath });
    setEditing(false);
    setSaveMsg(null);
  }

  function openWikilink(target: string): void {
    const cleaned = target.replace(/\.md$/i, '');
    const candidate = findFileByName(tree, cleaned);
    if (candidate) openFile(candidate.path);
  }

  useEffect(() => {
    if (!editing && file) {
      setDraft(file.file.raw);
      setDraftHash(hashData?.hash ?? null);
      setSaveMsg(null);
    }
  }, [file, editing, hashData]);

  function startEditing(): void {
    if (!file) return;
    setDraft(file.file.raw);
    setDraftHash(hashData?.hash ?? null);
    setEditing(true);
    setSaveMsg(null);
  }

  async function handleSave(): Promise<void> {
    if (!path) return;
    try {
      const res = await updateFile.mutateAsync({
        path,
        content: draft,
        expected_hash: draftHash,
      });
      setSaveMsg(`✓ Salvo às ${new Date().toLocaleTimeString('pt-BR')}`);
      setDraftHash(res.hash);
    } catch (err) {
      // A api tbm pode retornar 409 via ApiError
      const msg = (err as Error).message ?? 'Erro desconhecido';
      if (msg.includes('CONFLICT') || msg.includes('modificado')) {
        setSaveMsg(
          '⚠ Conflito — arquivo foi editado externamente. Sua versão foi salva como .conflict.md ao lado. Recarregue.',
        );
      } else {
        setSaveMsg(`✗ ${msg}`);
      }
    }
  }

  async function handleDelete(): Promise<void> {
    if (!path) return;
    if (!confirm(`Excluir ${path}? A ação é irreversível.`)) return;
    await deleteFile.mutateAsync(path);
    setParams({});
    setEditing(false);
  }

  useEffect(() => {
    // Beforeunload warning se dirty
    function handler(e: BeforeUnloadEvent): void {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  useEffect(() => {
    // Cmd+S pra salvar no modo edição
    function onKey(e: KeyboardEvent): void {
      if (editing && (e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        void handleSave();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, draft, draftHash, path]);

  const tagsFiltered = useMemo(() => {
    if (!tagsData) return [];
    if (!tagFilter) return tagsData.tags.slice(0, 40);
    return tagsData.tags.filter((t) => t.tag.toLowerCase().includes(tagFilter.toLowerCase()));
  }, [tagsData, tagFilter]);

  return (
    <div className="h-full -m-6 flex flex-col">
      {/* Banner git */}
      {gitStatus?.available && (gitStatus.ahead ?? 0) > 0 && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-xs flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          <span>
            Vault com <strong>{gitStatus.ahead}</strong> commit{gitStatus.ahead !== 1 ? 's' : ''}{' '}
            local{gitStatus.ahead !== 1 ? 'is' : ''} não enviado{gitStatus.ahead !== 1 ? 's' : ''}
            {gitStatus.branch && ` (branch ${gitStatus.branch})`}
          </span>
        </div>
      )}

      <div className="flex-1 grid grid-cols-[240px_1fr_300px] overflow-hidden">
        <aside className="border-r border-border bg-card/50 overflow-hidden flex flex-col">
          <VaultTree root={tree} currentPath={path} onOpenFile={openFile} />
          {canEdit && (
            <div className="p-2 border-t border-border">
              <Button size="sm" variant="outline" className="w-full" onClick={() => setNewDocOpen(true)}>
                <Plus className="h-3.5 w-3.5" /> Novo doc
              </Button>
            </div>
          )}
        </aside>

        <main className="overflow-hidden flex flex-col">
          {!path ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <FileText className="h-10 w-10 text-muted-foreground mb-2" />
              <h2 className="text-lg font-semibold mb-1">Obsidian Vault</h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Selecione um arquivo na árvore, use <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">⌘K</kbd> pra buscar
                {canEdit && ', ou clique em "Novo doc"'}.
              </p>
            </div>
          ) : fileLoading || !file ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : (
            <>
              <header className="px-6 pt-4 pb-2 border-b border-border">
                <nav className="text-xs text-muted-foreground mb-1">
                  {file.file.folder.split('/').filter(Boolean).join(' › ') || 'Raiz'}
                </nav>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h1 className="text-xl font-semibold truncate">{file.file.name.replace(/\.md$/, '')}</h1>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mt-1">
                      <span>{(file.file.size / 1024).toFixed(1)} KB</span>
                      <span>·</span>
                      <span>{new Date(file.file.mtime).toLocaleDateString('pt-BR')}</span>
                      {file.item_id && (
                        <>
                          <span>·</span>
                          <Link
                            to={`/items/${file.item_id}`}
                            className="inline-flex items-center gap-0.5 text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" /> Ficha
                          </Link>
                        </>
                      )}
                      {dirty && <Badge variant="outline" className="text-[10px]">não salvo</Badge>}
                      {saveMsg && (
                        <span
                          className={cn(
                            'ml-2',
                            saveMsg.startsWith('✓') && 'text-emerald-500',
                            saveMsg.startsWith('⚠') && 'text-amber-500',
                            saveMsg.startsWith('✗') && 'text-destructive',
                          )}
                        >
                          {saveMsg}
                        </span>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      {editing ? (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={!dirty || updateFile.isPending}
                          >
                            <Save className="h-3.5 w-3.5" />
                            {updateFile.isPending ? 'Salvando…' : 'Salvar'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                            <Eye className="h-3.5 w-3.5" />
                            Ver
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={startEditing}>
                            <Edit className="h-3.5 w-3.5" /> Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleDelete}
                            title="Excluir"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </header>

              <div className="flex-1 min-h-0 overflow-hidden">
                {editing ? (
                  <MarkdownEditor
                    value={draft}
                    onChange={setDraft}
                    onWikilinkClick={openWikilink}
                  />
                ) : (
                  <div className="h-full overflow-y-auto p-6">
                    <article className="max-w-3xl mx-auto">
                      {file.file.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {file.file.tags.map((t) => (
                            <Badge key={t} variant="secondary" className="text-[10px]">#{t}</Badge>
                          ))}
                        </div>
                      )}
                      <MarkdownRenderer content={file.file.raw} onWikilinkClick={openWikilink} />
                    </article>
                  </div>
                )}
              </div>
            </>
          )}
        </main>

        <aside className="border-l border-border bg-card/50 overflow-y-auto">
          {file ? (
            <Tabs defaultValue="backlinks" className="p-3">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="backlinks" className="text-xs">
                  Links ({file.counts.backlinks + file.counts.outgoing})
                </TabsTrigger>
                <TabsTrigger value="tags" className="text-xs">Tags</TabsTrigger>
                <TabsTrigger value="history" className="text-xs">
                  Git ({commits?.total ?? 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="backlinks" className="space-y-3">
                <Section title={`Referenciado por (${file.counts.backlinks})`} icon={LinkIcon}>
                  {file.backlinks.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum.</p>
                  ) : (
                    <ul className="space-y-1">
                      {file.backlinks.map((b) => (
                        <li key={b.id}>
                          <Link to={`/items/${b.source_item_id}`} className="text-xs hover:text-primary truncate block">
                            {b.source_nome} <span className="text-muted-foreground">· {b.source_tipo}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
                <Section title={`Aponta pra (${file.counts.outgoing})`} icon={LinkIcon}>
                  {file.outgoing.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum.</p>
                  ) : (
                    <ul className="space-y-1">
                      {file.outgoing.map((o) => (
                        <li key={o.id}>
                          <Link to={`/items/${o.target_item_id}`} className="text-xs hover:text-primary truncate block">
                            {o.target_nome} <span className="text-muted-foreground">· {o.target_tipo}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </TabsContent>

              <TabsContent value="tags">
                <TagsCloud tags={tagsFiltered} filter={tagFilter} onFilterChange={setTagFilter} />
              </TabsContent>

              <TabsContent value="history">
                <Section title="Histórico git" icon={GitBranch}>
                  {!commits || commits.total === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum commit (vault pode não estar em git).</p>
                  ) : (
                    <ul className="space-y-2">
                      {commits.commits.map((c) => (
                        <li key={c.sha} className="border-l-2 border-border pl-2">
                          <div className="text-xs font-mono text-muted-foreground">
                            {c.shaShort} · {c.author}
                          </div>
                          <div className="text-xs">{c.message}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {new Date(c.date).toLocaleString('pt-BR')}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="p-3">
              <TagsCloud tags={tagsFiltered} filter={tagFilter} onFilterChange={setTagFilter} />
            </div>
          )}
        </aside>
      </div>

      <NewDocModal
        open={newDocOpen}
        onOpenChange={setNewDocOpen}
        onCreated={(newPath) => setParams({ path: newPath })}
      />
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof LinkIcon;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <Icon className="h-3 w-3" /> {title}
      </div>
      {children}
    </div>
  );
}

function TagsCloud({
  tags,
  filter,
  onFilterChange,
}: {
  tags: Array<{ tag: string; count: number }>;
  filter: string | null;
  onFilterChange: (v: string | null) => void;
}): JSX.Element {
  return (
    <div>
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
        <Tag className="h-3 w-3" /> Tags do vault
      </div>
      <input
        type="text"
        value={filter ?? ''}
        onChange={(e) => onFilterChange(e.target.value || null)}
        placeholder="Filtrar tag…"
        className="w-full h-7 text-xs rounded border border-input bg-background px-2 mb-2"
      />
      <div className="flex flex-wrap gap-1">
        {tags.slice(0, 60).map((t) => (
          <Badge
            key={t.tag}
            variant="outline"
            className={cn('text-[10px] cursor-default', t.count >= 10 ? 'border-primary/40' : 'border-border')}
          >
            #{t.tag} <span className="text-muted-foreground ml-1">{t.count}</span>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function findFileByName(
  tree:
    | { children?: Array<{ name: string; path: string; type: string; children?: unknown[] }> }
    | undefined,
  name: string,
): { path: string } | null {
  if (!tree?.children) return null;
  const target = name.toLowerCase();
  const stack: Array<typeof tree.children[number]> = [...tree.children];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if (node.type === 'file' && node.name.replace(/\.md$/i, '').toLowerCase() === target) {
      return { path: node.path };
    }
    if (node.type === 'folder' && Array.isArray(node.children)) {
      for (const c of node.children as Array<typeof tree.children[number]>) stack.push(c);
    }
  }
  return null;
}
